import { forwardRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  REPORTS_CONSTANTS,
  getVisibleReportMenuItems,
} from 'pages/ReportsPage/ReportsPage.constants';
import { isPremiumDashboardTierEnabledSelector } from 'store/selectors/featureFlagSelectors/isPremiumDashboardTierEnabledSelector';
import { useTranslation } from 'hooks/useTranslation';
import {
  ReportsInternalMenuLink,
  ReportsNavigationWrapper,
  ReportsNavItem,
  ReportsNavItemWrapper,
  ReportsNavSubLabel,
  ReportsNavTextColumn,
  ToggleReportsNavIcon,
} from 'pages/ReportsPage/components/ReportsNavigation/ReportsNavigation.styled';

export const ReportsNavigationComponent = forwardRef<HTMLDivElement>((props, ref) => {
  const location = useLocation();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const isPremiumDashboardTierEnabled = useSelector(isPremiumDashboardTierEnabledSelector);

  const enabledMenuItems = getVisibleReportMenuItems(isPremiumDashboardTierEnabled);

  const toggleCollapse = () => {
    setIsOpen(!isOpen);
  };

  const getActiveMenuItem = (): string | null => {
    return (
      REPORTS_CONSTANTS.MENU_ITEMS.find(
        (item) => location.pathname === item.path || location.pathname.startsWith(item.path + '/'),
      )?.id || null
    );
  };

  const activeMenuItemId = getActiveMenuItem();

  return (
    <ReportsNavigationWrapper
      {...props}
      ref={ref}
      $isOpen={isOpen}
      id="reports-navigation"
      isPendingApproval={false}
    >
      <ToggleReportsNavIcon
        iconName={isOpen ? 'ChevronLeft' : 'ChevronRight'}
        onClick={toggleCollapse}
        $isOpen={isOpen}
      />

      {enabledMenuItems.map((item) => {
        const isComingSoon = item.status === 'coming_soon';
        const isActive = activeMenuItemId === item.id;
        return (
          <ReportsInternalMenuLink
            key={item.id}
            to={item.path}
            $isActive={isActive}
            $isOpen={isOpen}
            aria-current={isActive ? 'page' : undefined}
            tabIndex={isOpen ? undefined : -1}
            aria-hidden={isOpen ? undefined : true}
          >
            <ReportsNavItemWrapper $isOpen={isOpen}>
              <ReportsNavItem title={item.label} $isComingSoon={isComingSoon}>
                <ReportsNavTextColumn>
                  <span>{item.label}</span>
                  {isComingSoon && (
                    <ReportsNavSubLabel>{t('reports:COMING_SOON')}</ReportsNavSubLabel>
                  )}
                </ReportsNavTextColumn>
              </ReportsNavItem>
            </ReportsNavItemWrapper>
          </ReportsInternalMenuLink>
        );
      })}
    </ReportsNavigationWrapper>
  );
});

ReportsNavigationComponent.displayName = 'ReportsNavigationComponent';
